import React, {
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Col, Nav, Row, Tab } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import { Can } from "../../Casl/Casl";

import { useAuth } from "../../Hooks/UseAuth";
import { useProject } from "../../Hooks/UseProject";
import { useTeams } from "@/Hooks/UseTeam";
import useTranslation from "../../Hooks/UseTranslation";

import {
  filterByColorCodes,
  filterByDate,
  filterByGroups,
  filterByStatus,
  filterByTypes,
  filterByUsers,
  getCollapsedGroups,
  getCollapsedTasks,
  getMacroResult,
} from "../../Helpers/ProgrammationHelper";

import ColorCodeActions from "../../Redux/Actions/ColorCodeActions";
import InterfaceActions from "../../Redux/Actions/InterfaceActions";

import TaskFilters from "../../Components/TaskFilters/TaskFilters";
import Icon from "../../Components/Icon/Icon";

import TasksGroupByMonth from "./TasksGroupByMonth";
import TaskGroupByState from "./TasksGroupByState";
import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentHeader from "@/Layouts/MainLayout/components/ContentHeader";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";
import ProgrammationManager from "@/Components/ProgrammationManager/ProgrammationManager";
import { useProgrammation } from "@/Hooks/UseProgrammation";
import { setMacroAction } from "@/Redux/Actions/MacroActions";

const ProjectTasksPage = (props: any) => {

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { ability } = useAuth()

  const { project_id, project } = useProject()

  const { openItem, createNewTask, programmationLoaded, loadProgrammation } = useProgrammation()

  const { teams, allInternalProfiles } = useTeams()

  const projectLoadStatus = useSelector((state: any) => state.ProjectsReducer.loadRowStatus)
  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const macro = useSelector((state: any) => state.MacroReducer);
  const interfaceHeights = useSelector(
    (state: any) => state.InterfaceReducer.heights
  );

  const savedFilters = localStorage.getItem(`${project_id}-taskpage-filters`);

  const [ filters, setFilters ] = useState(
    savedFilters
      ? JSON.parse(savedFilters)
      : {
        user: "",
        state: "",
        team: "",
        group: "",
        code: "",
        type: "",
        start: "",
        end: "",
      }
  );

  const [ tab, setTab ] = useState(
    localStorage.getItem("taskPageTab") ?? "by_status"
  );
  const [ height, setHeight ] = useState(0);

  const [ showTaskFilters, setShowTaskFilters ] = useState(false);
  const [ onlyShowMine, setOnlyShowMine ] = useState(
    localStorage.getItem("onlyShowMyTasks") === "true" || project.mySettings.showOnlyMyTasks
  );

  // const loadTeams = async () => {
  //   // await dispatch(TeamsActions.getResultAction(project_id));
  // };

  const loadColorCodes = async () => {
    await dispatch(ColorCodeActions.getResult(project._id));
  };

  const loadTasks = async (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      return {
        ...task,
        responsibles: task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp)),
        internalProfiles: task.internalProfiles.map((resp: any) => allInternalProfiles.find((profile: any) => profile._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      filters
    }

    let macroResult = getMacroResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      macroResult = filterByUsers(macroResult, users_ids)
    } else if (newFilters.user)
      macroResult = filterByUsers(macroResult, newFilters.user.split(','))

    if (newFilters.group) {

      macroResult = filterByGroups(macroResult, newFilters.group.split(','))
    }else if (newFilters.code)
      macroResult = filterByColorCodes(macroResult, newFilters.code.split(','))

    if (newFilters.type)
      macroResult = filterByTypes(macroResult, newFilters.type.split(','))

    if (newFilters.state)
      macroResult = filterByStatus(macroResult, newFilters.state.split(','))

    if (newFilters.start || newFilters.end)
      macroResult = filterByDate(macroResult, newFilters.start, newFilters.end)

    const collapsedGroups = getCollapsedGroups(project_id, 'macro')
    const collapsedTasks = getCollapsedTasks(project_id, 'macro')

    dispatch(setMacroAction(macroResult, project.startsAt, project.endsAt, collapsedGroups, collapsedTasks))
  }

  const handleTabChange = (tab: string) => {
    localStorage.setItem("taskPageTab", tab);

    setTab(tab);
  };

  const handleNewTaskButton = async () => {

    if (!macro.result.length) {
      const prompt = await Swal.fire({
        title: t('TASKS:CreateNewTaskAlertTitle'),
        text: t('TASKS:CreateNewTaskAlertContent'),
        icon: "question",
        confirmButtonText: t('TASKS:CreateNewTaskAlertConfirmButton'),
        showCancelButton: true,
        cancelButtonText: t('TASKS:CreateNewTaskAlertCancelButton'),
        width: "400px",
      });

      if (!prompt.isConfirmed) return;
    }

    createNewTask()
  };

  const hasActiveFilters = () => {
    const values = Object.values(filters);
    const found = values.findIndex((f) => f !== "");
    return !!(found >= 0);
  };

  const handleChangeFilters = (filters: any) => {
    localStorage.setItem(
      `${project_id}-taskpage-filters`,
      JSON.stringify(filters)
    );

    loadTasks(filters)
    setFilters(filters);
  };

  const handleToggleOnlyMyTasks = () => {
    localStorage.setItem("onlyShowMyTasks", onlyShowMine ? "false" : "true");

    setOnlyShowMine(!onlyShowMine);
  };

  const handleSelectTask = (item_id: string) => {

    console.log('item_id', item_id)

    if (!item_id) return

    openItem({ item_id, noRedirect: true })
  };

  useEffect(() => {

    if (project._id) {

      if (project.ability) ability.update(project.ability)

      // loadTeams();
      loadColorCodes();
    }
  }, [ project._id ]);

  useEffect(() => {

    if (projectLoadStatus === 'loaded') {

      if (project.ability) ability.update(project.ability)

      if (programmation.loadStatus === 'none') {

        loadProgrammation();
      }
    }
  }, [ projectLoadStatus, programmation.loadStatus ]);

  useEffect(() => {

    if (programmationLoaded) {

      if (macro.loadStatus === 'none') {

        loadTasks()
      }
    }
  }, [ programmationLoaded, macro.loadStatus])

  useEffect(() => {
    console.log('here')

    loadTasks()
  }, [onlyShowMine])

  useEffect(() => {

    const projectLoaded = projectLoadStatus === 'loaded'
    const programmationLoaded = programmation.loadStatus === 'loaded' || programmation.loadStatus === 'error'

    if (!projectLoaded || !programmationLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [ projectLoadStatus, programmation.loadStatus ])

  useLayoutEffect(() => {
    const updateHeight = () => {
      const contentBodyMargin = 0;

      const height =
        window.innerHeight -
        interfaceHeights.headerHeight -
        interfaceHeights.submenuHeight -
        interfaceHeights.contentHeaderHeight -
        contentBodyMargin -
        interfaceHeights.contentWrapPaddingTop -
        interfaceHeights.contentWrapPaddingBottom -
        interfaceHeights.footerHeight;

      setHeight(height);
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [ interfaceHeights ]);

  return (
    <ProjectLayout menuName="tasks" subMenuName="board">
      <ContentHeader title={t('TASKS:PageTitle')}>
        <div className="ul-widget__head-toolbar">
          <div className="text-right">
            <button className={`btn btn-link btn-link-header ${macro.loadResultStatus === 'loading' ? 'active' : ''} d-inline-block`}
              onClick={() => loadProgrammation()}
              disabled={macro.loadResultStatus === 'loading'}
            >
              <Icon name="sync" fas fw spin={macro.loadResultStatus === 'loading'} />
              <span className="d-none d-lg-inline">
                {t('TASKS:ToolbarRefreshbutton')}
              </span>
            </button>
            <Can I="create" a="task" ability={ability}>
              <button
                className="btn btn-link btn-link-header"
                onClick={() => handleNewTaskButton()}>
                <span className="icon-agregar_tarea"></span>
                <span className="d-none d-lg-inline">
                  {t('TASKS:ToolbarCreatebutton')}
                </span>
              </button>
            </Can>
            <button
              className={`btn btn-link btn-link-header ${hasActiveFilters() ? "active" : ""
                } mr-1`}
              onClick={() => setShowTaskFilters(true)}>
              <span className="icon-filtro"></span>
              <span className="d-none d-lg-inline">
                {t('TASKS:ToolbarFilterbutton')}
              </span>
            </button>
            <span title={project.showOnlyMyTasks ? t('TASKS:ToolbarShowOnlyMyTasksDisabled') : undefined}>
              <button
                className={`btn btn-link btn-link-header ${onlyShowMine ? "active" : ""
                  } mr-1`}
                onClick={() => handleToggleOnlyMyTasks()}
                disabled={project.mySettings.showOnlyMyTasks}
              >
                <span className="icon-tareas_usuario"></span>
                <span className="d-none d-lg-inline">
                  {t('TASKS:ToolbarShowOnlyMyTasksButton')}
                </span>
              </button>
            </span>
          </div>
        </div>
        <div className="ul-widget__head-nav">
          <Tab.Container>
            <Nav as="ul" className="nav-tabs nav-tabs-line nav-tabs-bold">
              <Nav.Item as="li">
                <Nav.Link
                  active={tab === "by_status"}
                  onClick={() => handleTabChange("by_status")}>
                  <span className="icon-orden_estado"></span> {' '}
                  <span className="d-none d-lg-inline">
                    {t('TASKS:ToolbarSortByStatus')}
                  </span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link
                  active={tab === "by_month"}
                  onClick={() => handleTabChange("by_month")}>
                  <span className="icon-orden_mes"></span> {' '}
                  <span className="d-none d-lg-inline">
                    {t('TASKS:ToolbarSortByMonths')}
                  </span>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Tab.Container>
        </div>
      </ContentHeader>
      <ContentBody noPaddingBottom noScroll={tab === "by_status"}>
        <Row>
          <Col className="mb-1">
            <Tab.Content className="no-padding">
              <Tab.Pane active={tab === "by_status"}>
                <TaskGroupByState
                  height={height}
                  tasks={macro.result}
                  filters={filters}
                  teams={teams}
                  onSelectTask={handleSelectTask}
                />
              </Tab.Pane>
              <Tab.Pane active={tab === "by_month"}>
                <TasksGroupByMonth
                  height={height}
                  tasks={macro.result}
                  // filters={filters}
                  teams={teams}
                  onSelectTask={handleSelectTask}
                />
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </ContentBody>

      <ProgrammationManager taskList={macro.result} />

      <TaskFilters
        show={showTaskFilters}
        type="tasks"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />
    </ProjectLayout>
  );
};

export default ProjectTasksPage;
